// B2sCompleteStep — STAFF FORK (ADR-076 §9 copy-fork).
//
// A deliberate copy of portal-subscriber/src/obw/steps/B2sCompleteStep.tsx for
// the staff-mounted B2S (Upgrade) journey. Running in StaffObwModal, the "Upgrade
// complete" success is an IN-MODAL state; pressing "Done" calls onNext, which the
// modal turns into a dashboard toast + in-place dismiss (return to the nexa-admin
// dashboard) — never a subscriber-portal page (ADR-077 mount-owns-exit; C3). The
// two copies drift independently; behavioural fixes are applied to both.
//
// ADR-078 §4.3 — B2S conversion bookend. On confirm it converts the EXISTING beta
// account to a paying subscriber via POST /portal/b2s/convert (T0, HMAC-validated
// by the B2S launch token, which carries the accountId): sets isBeta:false on the
// Account + owning Domains, stops the trial clock/tag by construction (derivation
// is gated on isBeta), moves setupState/subscription to the paying state, and
// ENTITLES every owning domain through the billing seam. Payment is the stub — no charge
// (ADR-073 Am.1 / D4). (Until 29 July 2026 it "recorded the selected tier" into a `tier`
// overlay ADR-099 D5 had already deleted, and granted no entitlement at all — punchlist
// B13/B14. The plan now lands where it is read: Domain.entitlementProduct.)
import { useState } from 'react';
import type { StepProps } from '../OBWWizard';
import { portalOriginFor } from '../portal-origin';
import { TIER_NAMES, type Tier } from '../../lib/promo-codes';

type Phase = 'confirm' | 'submitting' | 'done' | 'error';

interface ConvertResult {
  ok?: boolean;
  accountId?: string;
  /** The catalogue product actually applied. ⚠ NOT necessarily what this step SENT: the
   *  server prefers the plan stored on the beta account by the BOW `subscribe` step, and only
   *  falls back to the body for betas provisioned before that step existed. */
  product?: string;
  term?: string;
  /** 'beta-account' (the stored plan won) or 'request-fallback' (this step's answer was used). */
  planSource?: string;
  /** Domains that failed to entitle. Present only on a PARTIAL success — the account converted
   *  and some domains did not get their plan, which is a real state a person must be told
   *  about rather than a detail to swallow. */
  failedDomains?: Array<{ domainToken: string; reason: string }>;
  error?: string;
}

export function B2sCompleteStep({ data, onNext }: StepProps) {
  const b2sToken = (data?.b2sToken as string) ?? '';
  const selectedTier = (data?.selectedTier as Tier) ?? null;
  const commitment = (data?.commitment as string) ?? 'annual';
  const businessName = ((data?.businessName as string) ?? '').trim();
  const tierName = selectedTier ? TIER_NAMES[selectedTier] : '';

  const [phase, setPhase] = useState<Phase>('confirm');
  const [error, setError] = useState('');
  const [result, setResult] = useState<ConvertResult | null>(null);

  // The plan the SERVER applied, rendered from its catalogue product id. Deliberately a small
  // local map rather than an import: `TIER_NAMES` is keyed by tier code and this is the other
  // vocabulary (product id), and conflating the two is the exact mistake the server-side
  // translation exists to prevent.
  const PRODUCT_NAMES: Record<string, string> = {
    nexagrowth: 'NexaGrowth', nexasuccess: 'NexaSuccess', nexacontext: 'NexaContext',
  };
  const appliedPlanName = result?.product ? (PRODUCT_NAMES[result.product] ?? result.product) : '';

  async function convert() {
    setError('');
    if (!b2sToken) {
      setError('This upgrade session has no launch token. Please re-launch Upgrade from the Beta participants table.');
      return;
    }
    if (!selectedTier) {
      setError('Please go back and choose a plan to continue.');
      return;
    }
    setPhase('submitting');
    try {
      // The participant's own zone — seeded by StaffObwModal from the same zone the token was
      // minted in. A token minted in one zone is refused by every other zone's convert.
      const res = await fetch(`${portalOriginFor(data)}/portal/b2s/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `selectedTier` + `commitment` are the FALLBACK plan, used only when the account
        // carries no plan of its own. A beta onboarded through the current BOW already has
        // one, and the server prefers it — see ConvertResult.product.
        body: JSON.stringify({ token: b2sToken, selectedTier, commitment }),
      });
      const body = (await res.json()) as ConvertResult;
      if (!res.ok || body.error) {
        if (body.error === 'token_expired') {
          setError('This upgrade link has expired. Please re-launch Upgrade from the Beta participants table.');
        } else if (body.error === 'not_beta') {
          setError('This account is no longer a beta account — it may already have been upgraded.');
        } else if (body.error === 'plan_required') {
          setError('No plan is recorded for this account. Please go back and choose a plan to continue.');
        } else if (body.error === 'no_domains') {
          setError('This account has no domains, so there is nothing to subscribe. Add a domain before upgrading.');
        } else if (body.error === 'entitlement_failed') {
          // The account was NOT converted (it stays on the beta and stays convertible), so a
          // retry is safe and is the right advice.
          setError('The subscription could not be applied, so nothing was changed. Please try again.');
        } else {
          setError('Could not complete the upgrade. Please try again.');
        }
        setPhase('error');
        return;
      }
      setResult(body);
      setPhase('done');
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
      setPhase('error');
    }
  }

  if (phase === 'done') {
    return (
      <div className="obw-step-content">
        <div className="obw-ai-status" role="status" style={{ borderColor: 'var(--success)' }}>
          <span className="obw-ai-done" aria-hidden="true">✓</span>
          <span><strong style={{ color: 'var(--text)' }}>Upgrade complete</strong></span>
        </div>
        <p className="obw-subtitle">
          {businessName ? `${businessName} is` : 'This account is'} now a paying Nexanomics subscriber.
          The beta trial has ended and the account no longer appears on the Beta participants table.
        </p>
        <ul className="obw-steps-preview" aria-label="Converted">
          <li>
            <span className="obw-step-num" aria-hidden="true">✓</span>
            <span>
              {/* ⭐ THE SERVER'S ANSWER, NOT THIS STEP'S. The account's own stored plan wins
                  over a re-selection made here, so reporting `tierName` would tell the
                  operator they applied a plan the platform did not apply — the honest field
                  is what came back. `tierName` remains the fallback for the pre-plan betas
                  where this step's answer WAS the plan. */}
              <strong style={{ color: 'var(--text)' }}>{appliedPlanName || tierName || 'Plan applied'}</strong><br />
              <span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>Subscription active — billing is stubbed (no charge in this build)</span>
            </span>
          </li>
          {(result?.failedDomains?.length ?? 0) > 0 && (
            /* A PARTIAL success. The account converted, but one or more domains did not get
               their plan applied — so those domains are live on the baseline feature set while
               the customer believes they are on the chosen plan. Silence here is how a
               half-entitled account becomes a support ticket nobody can explain. */
            <li>
              <span className="obw-step-num" aria-hidden="true">⚠️</span>
              <span>
                <strong style={{ color: 'var(--text)' }}>
                  {result?.failedDomains?.length} domain(s) did not receive the plan
                </strong><br />
                <span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>
                  Report this so the subscription can be applied to them — the rest of the account is active.
                </span>
              </span>
            </li>
          )}
        </ul>
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() => onNext({
            convertedAccountId: result?.accountId ?? '',
            convertedProduct: result?.product ?? '',
            convertedTerm: result?.term ?? '',
          })}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Confirm upgrade</h2>
      <p className="obw-subtitle">
        Convert this beta participant to a paying subscriber. The beta trial ends, the lifecycle tag
        stops, and the account moves to the selected plan.
      </p>

      <ul className="obw-confirm-rows" aria-label="Review upgrade">
        <li>
          <span className="obw-confirm-row-label">Account</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{businessName || 'Beta account'}</strong>
          </span>
        </li>
        <li>
          <span className="obw-confirm-row-label">Plan</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{tierName || '— no plan selected —'}</strong>
          </span>
        </li>
      </ul>

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <button
        type="button"
        className="obw-btn-primary"
        onClick={() => void convert()}
        disabled={phase === 'submitting'}
      >
        {phase === 'submitting' ? 'Converting…' : 'Convert to subscriber'}
      </button>
    </div>
  );
}
